/**
 * MobileBootstrap — one-time boot logic for the Capacitor iOS/Android shell.
 *
 * Runs on app mount. If the app is running inside a native Capacitor
 * container, it will:
 *   1. Request push notification permission.
 *   2. Register with APNs (iOS, wrapped by Firebase) or FCM (Android).
 *   3. POST the received device token to /api/push/register-fcm so
 *      send_push_to_users can fan out to this device.
 *   4. Wire tap-through routing when a push notification opens the app.
 *
 * On the web/PWA path this component is a no-op — the Capacitor.isNativePlatform
 * check short-circuits everything so bundling this into the web build is safe.
 */
import { useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

let bootstrapped = false; // guard against double-init on strict-mode remounts

export default function MobileBootstrap() {
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      if (bootstrapped) return;
      let Capacitor;
      try {
        // Dynamic import — @capacitor/core is bundled but its behaviour is
        // side-effect free on web. The .isNativePlatform() call cheaply
        // returns false on a browser.
        Capacitor = (await import("@capacitor/core")).Capacitor;
      } catch { return; }
      if (!Capacitor?.isNativePlatform || !Capacitor.isNativePlatform()) return;
      if (!user) return; // wait for auth — the token needs to be tied to a user
      bootstrapped = true;

      let PushNotifications;
      try {
        PushNotifications = (await import("@capacitor/push-notifications")).PushNotifications;
      } catch { return; }

      const perm = await PushNotifications.requestPermissions();
      if (perm.receive !== "granted") return;
      await PushNotifications.register();

      PushNotifications.addListener("registration", async (token) => {
        const platform = Capacitor.getPlatform(); // "ios" | "android"
        try {
          await api.post("/push/register-fcm", {
            token: token.value,
            platform,
            device_id: `${platform}-${token.value.slice(0, 16)}`,
          });
        } catch (e) {
          // Swallow — retry naturally on next app open.
        }
      });

      PushNotifications.addListener("registrationError", () => {
        // No-op — the in-app bell still delivers regardless.
      });

      // Tap-through: when the user opens a push, route them to the target URL.
      PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
        const target = event?.notification?.data?.url;
        if (target && typeof target === "string") {
          window.location.assign(target);
        }
      });
    })();
  }, [user]);

  return null; // headless
}
